/* global Excel */

import { ArticleResult, LookupType } from "../api/types";
import { resizeImageIfNeeded } from "./resizeImage";

const MAX_THUMBNAIL_SIZE_KB = 100;

interface PreparedThumbnail {
  row: number;
  base64: string;
  mimeType: string;
  width: number;
  height: number;
}

/** Maps a MIME type to the Base64EncodedImage type literal required by the Excel API. */
function toImageType(mimeType: string): "PNG" | "JPG" | "GIF" | "BMP" | "TIF" | "WEBP" | "ICO" {
  switch (mimeType) {
    case "image/jpeg": return "JPG";
    case "image/gif":  return "GIF";
    case "image/bmp":  return "BMP";
    case "image/tiff": return "TIF";
    case "image/webp": return "WEBP";
    default:           return "PNG";
  }
}

/**
 * Inserts thumbnail images as cell-native LocalImage values into the worksheet.
 * Pre-resizes images outside of Excel.run, then writes all cell values in a
 * single batch. Images are embedded in the workbook and follow row reordering
 * (sort/filter) automatically without any event-driven re-sync.
 */
export async function insertThumbnails(
  articles: ArticleResult[],
  articleRowMap: Map<string, number>,
  thumbnailColumn: string,
  thumbnailSize: number,
  fitToCell: boolean,
  lookupType: LookupType = "ArticleCode"
): Promise<void> {
  // Step 1: Pre-resize all images outside of Excel.run
  const prepared: PreparedThumbnail[] = [];
  for (const article of articles) {
    if (!article.thumbnail) continue;
    const key = lookupType === "Barcode" ? article.barcode : article.articleCode;
    if (!key) continue;
    const row = articleRowMap.get(key);
    if (row === undefined) continue;

    const imageData = await resizeImageIfNeeded(
      article.thumbnail.base64,
      article.thumbnail.mimeType,
      MAX_THUMBNAIL_SIZE_KB
    );
    prepared.push({
      row,
      base64: imageData.base64,
      mimeType: imageData.mimeType,
      width: imageData.width,
      height: imageData.height,
    });
  }

  if (prepared.length === 0) return;

  // Step 2: Insert into Excel as cell-native images
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();

      const cells = prepared.map(({ row }) =>
        sheet.getRange(`${thumbnailColumn}${row}`)
      );

      if (fitToCell) {
        // Set column width from the widest aspect ratio; set each row to thumbnailSize height
        const maxRenderedWidth = Math.max(
          ...prepared.map(({ width, height }) =>
            height > 0 ? thumbnailSize * (width / height) : thumbnailSize
          )
        );
        cells[0].format.columnWidth = maxRenderedWidth;
        for (const cell of cells) {
          cell.format.rowHeight = thumbnailSize;
        }
        await context.sync();
      }

      // Write each image as an embedded LocalImageCellValue
      for (let i = 0; i < prepared.length; i++) {
        const { base64, mimeType } = prepared[i];
        const cellValue: Excel.LocalImageCellValue = {
          type: "LocalImage",
          image: {
            type: toImageType(mimeType),
            data: base64,
          },
        };
        cells[i].valuesAsJson = [[cellValue as unknown as Excel.CellValue]];
      }

      await context.sync();
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to insert thumbnails into Excel: ${message}`);
  }
}
